import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IoHeart, IoCart, IoStar, IoCheckmark, IoTime } from 'react-icons/io5';
import { type Product } from '@/hooks/useProducts';
import useCartStore from '@/stores/CartStore';
import useWishlistStore from '@/stores/WishlistStore';
import Button from '@/components/common/Button/Button';
import { sampleProductImage } from '@/utils/constants/common'

import './ProductCard.css';

interface ProductCardProps {
    product: Product;
    variant?: 'default' | 'featured' | 'compact';
    showDescription?: boolean;
    showFeatures?: boolean;
    showActions?: boolean;
    animationDelay?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({
    product,
    variant = 'default',
    showDescription = false,
    showFeatures = true,
    showActions = true,
    animationDelay = 0
}) => {
    const navigate = useNavigate();
    const { addItem: addToCart } = useCartStore();
    const { addItem: addToWishlist, removeItem: removeFromWishlist, hasItem: isInWishlist } = useWishlistStore();

    const handleProductClick = () => {
        navigate(`/products/${product.id}`);
    };

    const handleWishlistToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isInWishlist(product.id)) {
            removeFromWishlist(product.id);
        } else {
            addToWishlist({
                id: product.id,
                product_id: product.id,
                product_name: product.name,
                product_image: product.main_image_url || sampleProductImage,
                base_price: product.base_price,
                category_name: product.category?.name
            });
        }
    };

    const handleAddToCart = (e: React.MouseEvent) => {
        e.stopPropagation();
        addToCart({
            product_id: product.id,
            product_name: product.name,
            product_image: product.main_image_url || sampleProductImage,
            base_price: product.base_price,
            quantity: product.min_order_quantity || 1,
            unit_price: product.base_price,
            customization: {}
        });
    };

    const cardClassName = `product_card product_card_${variant}`;
    const cardStyle = animationDelay > 0 ? { '--animation-delay': `${animationDelay}s` } as React.CSSProperties : {};

    return (
        <div className={cardClassName} style={cardStyle}>
            <div className="product_image_container">
                <img
                    src={product.main_image_url || sampleProductImage}
                    alt={product.name}
                    className="product_image"
                    onClick={handleProductClick}
                />
                
                {variant === 'featured' && (
                    <div className="product_badge">Featured</div>
                )}
                
                <button
                    className={`product_wishlist_btn ${isInWishlist(product.id) ? 'active' : ''}`}
                    onClick={handleWishlistToggle}
                >
                    <IoHeart size={variant === 'compact' ? 16 : 20} />
                </button>

                {variant === 'default' && (
                    <div className="product_actions_overlay">
                        <button 
                            className="cart_btn"
                            onClick={handleAddToCart}
                        >
                            <IoCart size={16} />
                        </button>
                    </div>
                )}
            </div>
            
            <div className="product_info">
                <div className="product_category">{product.category?.name}</div>
                <h3 className="product_name" onClick={handleProductClick}>
                    {product.name}
                </h3>
                
                {showDescription && (
                    <p className="product_description">{product.short_description || product.description}</p>
                )}
                
                <div className="product_rating">
                    <div className="stars">
                        {[1, 2, 3, 4, 5].map(star => (
                            <IoStar key={star} className="star filled" />
                        ))}
                    </div>
                    <span className="rating_text">4.8 (124)</span>
                </div>
                
                {showFeatures && (
                    <div className="product_features">
                        {product.allows_text_customization && (
                            <div className="feature_tag">
                                <span>Texto Personalizado</span>
                            </div>
                        )}
                        {product.allows_logo_upload && (
                            <div className="feature_tag">
                                <span>Logo Personalizado</span>
                            </div>
                        )}
                        {product.allows_pattern_upload && (
                            <div className="feature_tag">
                                <span>Patrón Personalizado</span>
                            </div>
                        )}
                        <div className="feature_tag quality">
                            <IoCheckmark size={12} />
                            <span>Calidad Garantizada</span>
                        </div>
                        <div className="feature_tag shipping">
                            <IoTime size={12} />
                            <span>Envio Rápido</span>
                        </div>
                    </div>
                )}
                
                <div className="product_footer">
                    <div className="product_price">
                        <span className="current_price">${product.base_price}</span>
                        <span className="price_label">Precio Inicial</span>
                    </div>
                    
                    {showActions && variant !== 'compact' && (
                        <div className="product_actions">
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleProductClick}
                            >
                                Ver Detalles
                            </Button>
                            {variant === 'default' && (
                                <Button 
                                    variant="secondary" 
                                    size="sm"
                                    onClick={handleAddToCart}
                                >
                                    Agregar al Carrito
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
